import React, { useEffect, useState } from 'react';
import { Popup } from 'components/common/popup/Popup';
import { alpha, Box, Button, DialogActions, Stack, Theme, Alert } from '@mui/material';
import { FormRendererAPIType } from 'components/common/form';
import { useForm } from 'react-hook-form';
import { alertRuleAPI } from 'api/alert-wizard';
import { AlertRulePopupProps } from 'components/alert-rule-popup/index';

import {
    alertRule,
    AlertRulePrepareData,
    AlertRulePrepareFormData,
    alertRuleWizard,
    AlertRuleWizardPrepareData,
    getAlertRuleWizardInitialData,
    screenType,
} from 'components/alert-rule-popup/AlertRuleDataProcessor';
import AlertRuleScreenType from 'components/alert-rule-popup/screens/AlertRuleScreenType';
import AlertRuleScreenWorkflow from 'components/alert-rule-popup/screens/AlertRuleScreenWorkflow';
import AlertRuleScreenMethod from 'components/alert-rule-popup/screens/AlertRuleScreenMethod';
import AlertRuleScreenContent from 'components/alert-rule-popup/screens/AlertRuleScreenContent';
import AlertRuleScreenCriteria from 'components/alert-rule-popup/screens/AlertRuleScreenCriteria';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';

export default function AlertRulePopup(props: AlertRulePopupProps) {
    const { show, onClose = () => {}, onFinish = () => {} } = props;

    const [alertRuleData, setAlertRuleData] = useState<alertRule>();
    const [wizardData, setWizardData] = useState<alertRuleWizard>(getAlertRuleWizardInitialData());
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { t } = useBundleTranslation(['components/alert-rule-popup/alert_rule_popup']);

    useEffect(() => {
        updateWizardData({ step: props.step ?? 1 });
        if (show)
            alertRuleAPI
                .getData(props)
                .then((response) => {
                    setAlertRuleData(AlertRulePrepareData(response.data));
                    setWizardData(AlertRuleWizardPrepareData(response.data));
                })
                .catch(() => {});
    }, []);

    useEffect(() => {
        updateWizardData({ indexStep: wizardData.step - 1 });
    }, [wizardData.step]);

    const updateWizardData = (data: any) => {
        setWizardData((prevWizardData) => {
            return { ...prevWizardData, ...data };
        });
    };
    const updateAlertRuleData = (data: any) => {
        setAlertRuleData((prevAlertRuleData) => {
            return { ...prevAlertRuleData, ...data };
        });
    };

    useEffect(() => {
        //id="warningAlertRulePhone" condition to show
        const isShowWarningAlertRulePhoneValue =
            'Y' == alertRuleData?.immediate_notification_ind &&
            'phone_alert' == alertRuleData?.send_notification_via &&
            !wizardData.isPhoneAlert;

        updateWizardData({ isShowWarningAlertRulePhone: isShowWarningAlertRulePhoneValue });
    }, [alertRuleData?.immediate_notification_ind, alertRuleData?.send_notification_via, wizardData.isPhoneAlert]);

    const { setValue: hookFormSetValue, control, getValues: hookFromGetValues, watch } = useForm({});
    const formActions = {
        hookFormControl: control,
        hookFormGetValues: hookFromGetValues,
        hookFormSetValue: hookFormSetValue,
        hookFormWatch: watch,
        formDidMount: true,
    } as FormRendererAPIType;

    const validateFields = (skipOnFieldChange?: boolean) => {
        let errors: string[] = [];
        let validationErrorFields: any = {};

        if (!alertRuleData) {
            return {
                error: errors,
                validationErrorField: validationErrorFields,
            };
        }

        if (!['compound', 'unusual value', 'best ever', 'worst ever'].includes(alertRuleData.alert_type)) {
            if (alertRuleData.frequently_condition_values < 1) {
                errors.push(t(`errors.specify_value_for_total_number_greater_than_0`));
                validationErrorFields.frequently_condition_values = true;
            } else if (alertRuleData.frequently_condition_satisfied < 1) {
                errors.push(t(`errors.specify_value_for_required_number_greater_than_1`));
                validationErrorFields.frequently_condition_satisfied = true;
            } else if (alertRuleData.frequently_condition_values >= 1000) {
                errors.push(t(`errors.value_for_total_number_must_be_less_then_1000`));
                validationErrorFields.frequently_condition_values = true;
            } else if (alertRuleData.frequently_condition_satisfied >= 1000) {
                errors.push(t(`errors.alert_specify_value_for_required_number_less_then_1000`));
                validationErrorFields.frequently_condition_satisfied = true;
            } else if (alertRuleData.frequently_condition_values < alertRuleData.frequently_condition_satisfied) {
                errors.push(t(`errors.alert_specify_value_for_required_number_less_than_total_number`));
                validationErrorFields.frequently_condition_values = true;
                validationErrorFields.frequently_condition_satisfied = true;
            } else if (
                alertRuleData.frequently_condition_satisfied == 1 &&
                alertRuleData.frequently_condition_values != 1
            ) {
                errors.push(
                    t(`errors.alert_condition_is_not_allowed`, {
                        num: alertRuleData.frequently_condition_satisfied,
                    }),
                );
                validationErrorFields.frequently_condition_values = true;
                validationErrorFields.frequently_condition_satisfied = true;
            }

            if (['fixed', 'less fixed'].includes(alertRuleData.comparison_type)) {
                if (String(alertRuleData.comparison_fixed_value) == '')
                    validationErrorFields.comparison_fixed_value = true;
            } else {
                if (String(alertRuleData.comparison_pct_tolerance) == '')
                    validationErrorFields.comparison_pct_tolerance = true;
            }
        }

        switch (alertRuleData.alert_type) {
            case 'volatility band':
                if (
                    alertRuleData.comparison_fixed_value > alertRuleData.comparison_fixed_value_2 &&
                    alertRuleData.pct_comparison_type == 'between'
                ) {
                    errors.push(t(`errors.alert_between_values_incorrect`));
                }
                break;
            case 'compound':
                if (!skipOnFieldChange) {
                    if (alertRuleData.user_defined_name == '') {
                        errors.push(t(`errors.alert_name_required`));
                    }
                    if (alertRuleData.alert_rule_count_type !== 'all') {
                        if (alertRuleData.alert_rule_count == 0) {
                            errors.push(t(`errors.alert_specify_number_of_selected_alert_rules`));
                            validationErrorFields.alert_rule_count = true;
                        } else if (alertRuleData.alert_rule_count > alertRuleData.compound_selected_alert_rule.length) {
                            errors.push(t(`errors.alert_number_of_specified_alert_rules_is_invalid`));
                            validationErrorFields.alert_rule_count = true;
                        }
                    }
                    if (
                        !alertRuleData.compound_selected_alert_rule ||
                        alertRuleData.compound_selected_alert_rule.length == 0
                    ) {
                        errors.push(t(`errors.alert_alert_rules_must_be_specified_for_compound_alert`));
                    }
                }
                break;
        }

        return {
            error: errors,
            validationErrorField: validationErrorFields,
        };
    };

    const showValidationErrors = (validationResult: any) => {
        if (validationResult.error.length) {
            updateWizardData({
                error: validationResult.error,
                validationErrorField: validationResult.validationErrorField,
            });
        }
    };

    useEffect(() => {
        updateWizardData({ error: [], validationErrorField: {} });
        const result = validateFields(true);
        showValidationErrors(result);
    }, [
        alertRuleData?.comparison_type,
        alertRuleData?.exclude_anomaly_points_ind,
        alertRuleData?.comparison_fixed_value,
        alertRuleData?.frequently_condition_satisfied,
        alertRuleData?.frequently_condition_values,
        alertRuleData?.comparison_interval,
        alertRuleData?.comparison_interval_compare_to,
        alertRuleData?.unusual_value_detection_method,
        alertRuleData?.trigger_alert_when_frequently,
        alertRuleData?.pct_comparison_type,
        alertRuleData?.range_of_values_scope,
        alertRuleData?.target_id,
        alertRuleData?.compare_line_id,
        alertRuleData?.comparison_interval_compare_to_period,
        alertRuleData?.comparison_interval_period,
    ]);

    useEffect(() => {
        //clean errors on change field
        const currentErrors = wizardData.error.length;
        if (currentErrors > 0) {
            const result = validateFields();
            if (currentErrors > result.error.length) {
                updateWizardData({ error: [], validationErrorField: {} });
            }
        }
    }, [
        alertRuleData?.user_defined_name,
        alertRuleData?.alert_rule_count_type,
        alertRuleData?.alert_rule_count,
        alertRuleData?.compound_selected_alert_rule,
    ]);

    useEffect(() => {
        updateWizardData({ error: [], validationErrorField: {} });
    }, [alertRuleData?.alert_type]);

    if (!alertRuleData || !show) return null;

    const currentScreenKey: screenType = wizardData.screens[wizardData.indexStep].key;
    const nextScreenKey = wizardData.screens[wizardData.step]?.key;
    const alertTypeKey = alertRuleData.alert_type.replaceAll(' ', '_');

    const steps = wizardData.screens.map((screenItem) => {
        return {
            label: t(`screen.${screenItem.key}.step_label`),
            name: `${screenItem.key}`,
        };
    });

    const skipThisStep = (stepNum: number) => {
        const newStepScreenKey = wizardData.screens[stepNum - 1]?.key;

        return (
            ['unusual value', 'best ever', 'worst ever'].includes(alertRuleData.alert_type) &&
            newStepScreenKey === 'criteria'
        );
    };

    const SCREEN_COMPONENTS: { [key in screenType]: any } = {
        type: AlertRuleScreenType,
        criteria: AlertRuleScreenCriteria,
        content: AlertRuleScreenContent,
        workflow: AlertRuleScreenWorkflow,
        method: AlertRuleScreenMethod,
    };

    const goNextStep = () => {
        //validate data: For error don't move to next step
        if (wizardData.step >= 2) {
            const result = validateFields();
            if (result.error.length > 0) {
                showValidationErrors(result);
                return false;
            }
        }
        if (nextScreenKey) {
            let newStep = wizardData.step + 1;
            if (skipThisStep(newStep)) {
                newStep += 1;
            }
            updateWizardData({ step: newStep });
        } else {
            if (
                !wizardData.error &&
                Number(wizardData.issues_to_close) > 0 &&
                wizardData.init_workflow_id != alertRuleData?.workflow_id
            ) {
                //ToDo Change to our confirm ui popup
                if (!confirm(t('form.common.alert_assigning_new_workflow_to_alert'))) return false;
            }
            setIsLoading(true);
            const actionType = wizardData.isNew ? 'add' : 'update';
            alertRuleAPI[actionType](AlertRulePrepareFormData(alertRuleData, wizardData)).then((response: any) => {
                onFinish(response);
            });
        }
    };

    const dialogActions = (
        <DialogActions sx={{ justifyContent: 'space-between', py: 2, px: 3 }}>
            <Box>
                {wizardData.step == 1 ? (
                    <Button variant={'outlined'} onClick={onClose} color={'neutral'}>
                        {t('btn_cancel')}
                    </Button>
                ) : (
                    <Button
                        variant={'outlined'}
                        onClick={() => {
                            let prevStep = wizardData.step - 1;
                            if (skipThisStep(prevStep)) {
                                prevStep -= 1;
                            }
                            updateWizardData({ step: prevStep });
                        }}
                        color={'neutral'}
                        startIcon={<IconMi icon="chevron-left" />}
                    >
                        {t('btn_back')}
                    </Button>
                )}
            </Box>
            <Box>
                <Button
                    data-test={nextScreenKey ? `alert-rule-next-step_button` : 'alert-rule-finish_button'}
                    onClick={goNextStep}
                    color={!nextScreenKey ? 'success' : undefined}
                    disabled={wizardData.step != 1 && wizardData.error.length > 0}
                >
                    {nextScreenKey
                        ? t(`screen.${nextScreenKey}.btn_to_this_step`)
                        : Number(alertRuleData.workflow_id)
                          ? t('btn_finish_workflow')
                          : t('btn_finish')}
                </Button>
            </Box>
        </DialogActions>
    );

    return (
        <Popup
            settings={{
                title: t(`screen.${currentScreenKey}.title`, {
                    alert_rule_type_name: t(`ar_type.${wizardData.isKpi ? 'kpi' : 'global'}`),
                    alert_type_name: t(`alert_type.${alertTypeKey}.label`),
                }),
                noButtons: true,
                customActionPanel: dialogActions,
            }}
            open={show}
            onHide={onClose}
            maxWidth={wizardData.step == 1 ? 'popupMd' : 'popupLg'}
            scrollType={'body'}
            onPopupKeyDownEnter={(event) => {
                if (!isLoading) goNextStep();
            }}
        >
            {isLoading && <LoadingPlaceholder variant={'secondary'} relativeToParent sx={{ zIndex: 1 }} />}
            <Stack
                sx={{
                    mx: -3,
                    mt: -2,
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    py: 1,
                    px: 3,
                }}
                direction={'row'}
                spacing={1}
            >
                {steps.map((stepItem, index) => {
                    return (
                        <Box
                            key={stepItem.name}
                            onClick={() => {
                                //validate data: For error show step 2
                                if (index > 1) {
                                    const result = validateFields();
                                    if (result.error.length > 0) {
                                        showValidationErrors(result);
                                        updateWizardData({ step: 2 });
                                        return false;
                                    }
                                }

                                const newStep = index + 1;
                                if (!skipThisStep(newStep)) {
                                    updateWizardData({ step: newStep });
                                }
                            }}
                            className={`step-item ${wizardData.step === index + 1 ? 'step-item--active' : ''}`}
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                '&:not(.step-item--active):hover': {
                                    cursor: 'pointer',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: (theme) => `1px solid ${theme.palette.primary.main}`,
                                    color: 'primary.main',
                                    borderRadius: 1,
                                    mr: 1,
                                    '.step-item--active &, .step-item:not(.step-item--active):hover &': {
                                        backgroundColor: 'primary.main',
                                        color: 'background.default',
                                        fontWeight: '600',
                                    },
                                }}
                                className={wizardData.step === index + 1 ? 'active' : undefined}
                            >
                                {index + 1}
                            </Box>
                            <Box
                                sx={{
                                    color: 'primary.main',
                                    '.step-item:not(.step-item--active):hover &': {
                                        opacity: 0.75,
                                    },
                                }}
                            >
                                {stepItem.label}
                            </Box>
                            {steps.length != index + 1 && (
                                <IconMi
                                    icon={'chevron-right'}
                                    sx={{
                                        ml: 0.5,
                                        fontSize: '16px',
                                        color: (theme: Theme) => alpha(theme.palette.text.primary, 0.24),
                                    }}
                                />
                            )}
                        </Box>
                    );
                })}
            </Stack>

            <Box sx={{ height: !wizardData.isGlobal ? 496 : 300, mb: -2, mx: -3, px: 3, overflow: 'auto', py: 3 }}>
                {wizardData.step != 1 && wizardData.error.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity={'error'} variant={'standard'}>
                            {wizardData.error.map((errorItem) => (
                                <Box key={errorItem}>{errorItem}</Box>
                            ))}
                        </Alert>
                    </Box>
                )}

                {currentScreenKey &&
                    React.createElement(SCREEN_COMPONENTS[currentScreenKey], {
                        alertRuleData: alertRuleData,
                        wizardData: wizardData,
                        updateAlertRuleData: updateAlertRuleData,
                        updateWizardData: updateWizardData,
                    })}
            </Box>
        </Popup>
    );
}
